/* Measurement estimates only. Products, prices and purchase rules are rechecked in the native session. */
(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root.document) root.SaberMaterialCalculator = api;
})(typeof window === 'undefined' ? globalThis : window, function (root) {
  'use strict';
  const modes = Object.freeze({
    paint: { coverage: 350, unit: 'sq. ft.', help: 'Example only: 350 sq. ft. per gallon per coat. Replace this with the coverage of the exact can or container you plan to buy.' },
    flooring: { coverage: 20, unit: 'sq. ft.', help: 'Example only: 20 sq. ft. per box. Enter the coverage of the exact box or purchased unit. Underlayment and trim are separate.' },
    drywall: { coverage: 32, unit: 'sq. ft.', help: 'Example only: 32 sq. ft. per 4 × 8 ft. sheet. Adjust for the sheet you choose. Fasteners, joint tape and compound are separate.' },
    tile: { coverage: 10, unit: 'sq. ft.', help: 'Example only: 10 sq. ft. per box. Enter the actual coverage per box or single tile, depending on how the product is sold. Grout and adhesive are separate.' },
    caulk: { coverage: 30, unit: 'linear ft.', help: 'Example only: 30 linear ft. per tube. Joint width, depth and tube size change the coverage; use the product’s actual guidance.' },
  });
  function numeric(value, min, max, name) {
    if (String(value).trim() === '' || !Number.isFinite(Number(value)) || Number(value) < min || Number(value) > max)
      throw new Error('Enter a valid ' + name + '.');
    return Number(value);
  }
  function calculate(input) {
    const material = input.material;
    if (!modes[material]) throw new Error('Choose a material.');
    const coverage = numeric(input.coverage, 0.001, 1e8, 'coverage per purchased unit');
    const waste = numeric(input.waste, 0, 100, 'extra-material percentage');
    let base;
    if (material === 'caulk') base = numeric(input.joint, 0.01, 1e8, 'joint length');
    else {
      const length = numeric(input.length, 0.01, 1e5, 'room length');
      const width = numeric(input.width, 0.01, 1e5, 'room width');
      const openings = numeric(input.openings, 0, 1e8, 'excluded area');
      base = length * width;
      if (material === 'paint' || material === 'drywall') {
        base = 2 * (length + width) * numeric(input.height, 0.01, 1e5, 'wall height');
        if (material === 'drywall' && input.ceiling) base += length * width;
      }
      if (openings >= base) throw new Error('Excluded area must be smaller than the measured area.');
      base -= openings;
      if (material === 'paint') {
        const coats = numeric(input.coats, 1, 10, 'number of coats');
        if (!Number.isInteger(coats)) throw new Error('Use a whole number of coats.');
        base *= coats;
      }
    }
    const total = base * (1 + waste / 100);
    const units = Math.ceil(total / coverage - 1e-10);
    if (!Number.isSafeInteger(units) || units < 1 || units > 1000000) throw new Error('This estimate is too large. Calculate a smaller section.');
    return { material, coverage, waste, base, total, units, unit: modes[material].unit };
  }
  function roundedQuantity(units, variant) {
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.increment) || 1);
    return Math.ceil(Math.max(units, min) / step) * step;
  }
  const element = (tag, text, attributes = {}) => {
    const el = root.document.createElement(tag);
    if (text !== undefined) el.textContent = text;
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, String(value)));
    return el;
  };
  class MaterialCalculator extends (root.HTMLElement || class {}) {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('[data-calc-form]');
      this.search = this.querySelector('[data-calc-search]');
      this.result = this.querySelector('[data-calc-result]');
      this.products = this.querySelector('[data-calc-products]');
      this.status = this.querySelector('[data-calc-status]');
      this.order = this.querySelector('[data-calc-order]');
      this.confirm = this.querySelector('[data-calc-confirm]');
      this.quantity = this.querySelector('[data-calc-quantity]');
      this.add = this.querySelector('[data-calc-add]');
      this.save = this.querySelector('[data-calc-save]');
      this.cartLink = this.querySelector('[data-calc-cart]');
      this.events = new AbortController();
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.events.signal });
      on(this.form.elements.material, 'change', () => this.setMode());
      on(this.form, 'input', () => this.invalidate());
      on(this.form, 'submit', event => { event.preventDefault(); this.estimate(); });
      on(this.search, 'submit', event => { event.preventDefault(); this.lookup(); });
      on(this.search.elements.sku, 'input', () => { this.controller?.abort(); this.sequence = (this.sequence || 0) + 1; this.variant = null; this.products.replaceChildren(); this.order.hidden = true; this.updateButtons(); });
      on(this.confirm, 'change', () => this.updateButtons());
      on(this.quantity, 'input', () => this.updateButtons());
      on(this.add, 'click', () => this.addToCart());
      on(this.save, 'click', () => this.saveList());
      this.setMode();
    }
    disconnectedCallback() { this.events?.abort(); this.controller?.abort(); this.sequence = (this.sequence || 0) + 1; this.ready = false; }
    setMode() {
      const material = this.form.elements.material.value;
      const visible = { area: material !== 'caulk', wall: ['paint', 'drywall'].includes(material), paint: material === 'paint', line: material === 'caulk', ceiling: material === 'drywall' };
      Object.entries(visible).forEach(([key, show]) => this.querySelectorAll('[data-' + key + '-field]').forEach(wrapper => {
        wrapper.hidden = !show; wrapper.querySelectorAll('input').forEach(input => { input.disabled = !show; });
      }));
      this.form.elements.coverage.value = modes[material].coverage;
      this.querySelector('[data-coverage-label]').textContent = 'Coverage per purchased unit (' + modes[material].unit + ')';
      this.querySelector('[data-coverage-help]').textContent = modes[material].help;
      this.invalidate();
    }
    invalidate() {
      this.calculation = null; this.confirm.checked = false;
      this.result.textContent = 'Measurements changed. Calculate again before ordering.';
      this.updateButtons();
    }
    message(text, error = false) { this.status.textContent = text; this.status.setAttribute('role', error ? 'alert' : 'status'); }
    estimate() {
      try {
        const values = Object.fromEntries(new FormData(this.form));
        values.ceiling = this.form.elements.ceiling.checked;
        this.calculation = calculate(values);
        this.uncertain = false; this.completed = false;
        const { units, total, unit, waste } = this.calculation;
        this.result.replaceChildren(element('strong', units.toLocaleString() + ' purchased unit' + (units === 1 ? '' : 's')), element('p', total.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + unit + ' including ' + waste + '% extra. Confirm coverage and pack size below.'));
        this.confirm.checked = false; this.prepareQuantity(); this.updateButtons(); this.message('Estimate ready. Choose your product and confirm its coverage.');
      } catch (error) { this.calculation = null; this.updateButtons(); this.message(error.message, true); }
    }
    async lookup() {
      if (this.busy) return;
      if (!root.SaberQuickOrder) return this.message('Product matching is unavailable. Refresh the page and try again.', true);
      this.controller?.abort(); this.controller = new AbortController();
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.variant = null; this.order.hidden = true; this.products.replaceChildren(); this.updateButtons();
      this.message('Checking current products…');
      try {
        const rows = await root.SaberQuickOrder.resolveRows([{ sku: this.search.elements.sku.value.trim(), quantity: 1 }], { searchUrl: this.dataset.searchUrl, signal: this.controller.signal });
        if (sequence !== this.sequence || !this.isConnected) return;
        const row = rows[0];
        if (!row || row.error) throw new Error(row?.error || 'No product was found.');
        if (!row.matches?.length) throw new Error('No matching item was found. Check the full SKU or model on the product page.');
        const label = element('label', 'Choose the exact product option');
        const select = element('select', undefined, { 'aria-label': 'Product option' });
        select.append(element('option', 'Select a product option', { value: '' }));
        row.matches.forEach((v, index) => select.append(element('option', v.productTitle + (v.variantTitle ? ' · ' + v.variantTitle : '') + ' · ' + (v.priceText || 'Check price') + (v.available ? '' : ' · unavailable'), { value: index })));
        const detail = element('div'); label.append(select);
        const box = element('div', undefined, { class: 'ss-materials__choice' }); box.append(label, detail); this.products.append(box);
        select.addEventListener('change', () => {
          this.variant = select.value === '' ? null : row.matches[Number(select.value)];
          this.confirm.checked = false; this.completed = false; this.uncertain = false;
          detail.replaceChildren();
          if (this.variant) {
            const url = new URL(this.variant.url, root.location.origin);
            if (url.origin === root.location.origin && /\/products\//.test(url.pathname)) {
              url.searchParams.set('variant', this.variant.id);
              detail.append(element('a', 'Check product specifications & coverage →', { href: url.href, target: '_blank', rel: 'noopener' }));
            }
            detail.append(element('p', (this.variant.priceLabel ? this.variant.priceLabel + ': ' : '') + this.variant.priceText));
          }
          this.order.hidden = !this.variant; this.prepareQuantity(); this.updateButtons();
          if (this.variant?.orderable === false) this.message(this.variant.orderBlockReason || 'Open the product page to order.', true);
          else if (this.variant?.available === false) this.message('This option is currently unavailable. Choose another item or contact us.', true);
          else this.message('Confirm the product coverage and calculated quantity before adding.');
        });
        this.message('Choose the exact product option, then check its coverage.');
      } catch (error) { if (error.name !== 'AbortError' && sequence === this.sequence) this.message(error.message, true); }
    }
    prepareQuantity() {
      if (!this.variant || !this.calculation) return;
      const quantity = roundedQuantity(this.calculation.units, this.variant);
      this.quantity.value = quantity; this.quantity.min = this.calculation.units; this.quantity.step = this.variant.increment;
      this.querySelector('[data-calc-rules]').textContent = 'Estimate: ' + this.calculation.units + ' unit(s). Product minimum: ' + this.variant.min + '; multiples of ' + this.variant.increment + (this.variant.max ? '; maximum ' + this.variant.max : '') + '. Existing cart quantities are checked before adding.';
    }
    selectedValid() {
      return this.calculation && this.variant?.available && this.variant.orderable !== false && !this.variant.requiresSellingPlan && this.confirm.checked && Number.isSafeInteger(Number(this.quantity.value)) && Number(this.quantity.value) >= this.calculation.units;
    }
    updateButtons() {
      const valid = this.selectedValid();
      this.add.disabled = !valid || this.busy || this.uncertain || this.completed;
      this.save.disabled = !valid || this.busy;
    }
    async cart() {
      const response = await root.fetch(this.dataset.cartUrl.replace(/\/$/, '') + '.js', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Your cart could not be checked. Open your cart before ordering.');
      const cart = await response.json(); root.SaberQuickOrder.cartQuantities(cart); return cart;
    }
    async addToCart() {
      if (this.busy || !this.selectedValid() || this.uncertain || this.completed) return;
      this.busy = true; this.updateButtons(); this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('input,select,button').forEach(control => { if (!control.disabled) { control.dataset.calcLocked = 'true'; control.disabled = true; } });
      let before, attempted = false, items;
      try {
        this.message('Rechecking your product, price and cart…');
        const chosen = this.variant;
        const [fresh] = await root.SaberQuickOrder.resolveRows([{ variantId: chosen.id, url: chosen.url, quantity: Number(this.quantity.value) }], { searchUrl: this.dataset.searchUrl });
        if (!fresh || fresh.error) throw new Error(fresh?.error || 'Recheck the product before ordering.');
        const aggregate = root.SaberQuickOrder.aggregateRows([fresh]);
        if (aggregate.errors.length) throw new Error(aggregate.errors.join(' '));
        items = aggregate.items;
        const current = items[0];
        if (current.price !== chosen.price || current.min !== chosen.min || current.increment !== chosen.increment || current.max !== chosen.max || JSON.stringify(current.priceBreaks || []) !== JSON.stringify(chosen.priceBreaks || [])) {
          this.variant = current; this.confirm.checked = false; this.prepareQuantity(); throw new Error('The price or quantity rules changed. Current price: ' + current.priceText + '. Check the product details and confirm coverage again.');
        }
        before = await this.cart();
        const errors = root.SaberQuickOrder.validateAggregate(items, before);
        if (errors.length) throw new Error(errors.join(' '));
        attempted = true;
        const response = await root.fetch(this.dataset.rootUrl + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items.map(item => ({ id: item.id, quantity: item.quantity, properties: { 'Project material': this.calculation.material, 'Estimated coverage': this.calculation.total.toFixed(2) + ' ' + this.calculation.unit, 'Coverage per unit': String(this.calculation.coverage), 'Extra material': this.calculation.waste + '%' } })) }) });
        if (!response.ok) { let payload; try { payload = await response.json(); } catch {} throw new Error(typeof payload?.description === 'string' ? payload.description : 'The cart did not confirm your addition.'); }
        const added = await response.json();
        const after = await this.cart();
        const changes = root.SaberQuickOrder.reconcile(items, before, after);
        if (changes.some(item => item.remaining)) { this.uncertain = true; throw new Error('The full quantity could not be confirmed. Review your cart before adding more.'); }
        this.completed = true; this.cartLink.hidden = false;
        this.message('Added ' + current.quantity + ' unit(s) to your cart. Review final prices and quantities in your cart.');
        root.SaberQuickOrder.QuickOrder.prototype.updateNativeCart(added, after);
      } catch (error) {
        if (attempted) { this.uncertain = true; this.cartLink.hidden = false; this.message(error.message + ' Review your cart before trying again to avoid duplicate quantities.', true); }
        else this.message(error.message, true);
      } finally {
        this.querySelectorAll('[data-calc-locked]').forEach(control => { control.disabled = false; delete control.dataset.calcLocked; });
        this.busy = false; this.removeAttribute('aria-busy'); this.updateButtons();
      }
    }
    saveList() {
      if (!this.selectedValid() || this.busy) return;
      try {
        if (!root.SaberSavedLists) throw new Error('Purchasing lists are unavailable. Refresh the page and try again.');
        root.SaberSavedLists.openSaveDialog([{ variantId: this.variant.id, url: this.variant.url, sku: this.variant.sku, quantity: Number(this.quantity.value) }]);
      } catch (error) { this.message(error.message, true); }
    }
  }
  if (root.customElements && !root.customElements.get('saber-material-calculator')) root.customElements.define('saber-material-calculator', MaterialCalculator);
  return { calculate, roundedQuantity, modes, MaterialCalculator };
});
